/**
 * Content lengths ("12:34", "1:02:03") as the app reads, stores and shows them.
 * One rule for every place that prints one, so the same item never reads as
 * "1 min" in one card and something else in the next.
 */

/** Minutes are rounded to the nearest whole one; anything under this many
 *  seconds would round to 0 and is shown as "<1 min" instead. */
export const UNDER_A_MINUTE_SECONDS = 30;

/** m:ss / mm:ss (minutes may exceed 59) or h:mm:ss. Seconds always 00-59. */
const MINUTES_SECONDS = /^(\d+):([0-5]\d)$/;
const HOURS_MINUTES_SECONDS = /^(\d+):([0-5]\d):([0-5]\d)$/;

/**
 * Seconds in an "m:ss", "mm:ss" or "h:mm:ss" length, zero included. Null only
 * when the length is missing or in none of those forms.
 */
const readContentLengthSeconds = (length: unknown): number | null => {
  if (typeof length !== 'string') {
    return null;
  }
  const value = length.trim();
  const hms = HOURS_MINUTES_SECONDS.exec(value);
  if (hms) {
    return Number(hms[1]) * 3600 + Number(hms[2]) * 60 + Number(hms[3]);
  }
  const ms = MINUTES_SECONDS.exec(value);
  if (ms) {
    return Number(ms[1]) * 60 + Number(ms[2]);
  }
  return null;
};

/**
 * Seconds in a content length: "m:ss", "mm:ss" or "h:mm:ss". Null when the
 * length is missing, not in one of those forms, or zero, so an unreadable
 * length is left out rather than shown as a wrong one.
 */
export const contentLengthSeconds = (length: unknown): number | null => {
  const seconds = readContentLengthSeconds(length);
  return seconds !== null && seconds > 0 ? seconds : null;
};

/**
 * A content length in its stored form, "m:ss": whole minutes with no leading
 * zero, then two-digit seconds ("8:38", "63:30"). Takes the forms
 * contentLengthSeconds reads, so "08:38" becomes "8:38" and "1:03:30" becomes
 * "63:30", and zero becomes "0:00". Null when the length can't be read.
 */
export const normalizeContentLength = (value: unknown): string | null => {
  const seconds = readContentLengthSeconds(value);
  if (seconds === null) {
    return null;
  }
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

export const CONTENT_LENGTH_FORMAT_ERROR =
  'Length must be minutes:seconds (e.g. 8:38) or hours:minutes:seconds (e.g. 1:03:30).';
export const CONTENT_LENGTH_REQUIRED_ERROR = 'Length is required for video and audio.';

// Types that play: without a length their progress bars and watch-time totals break.
const TIMED_CONTENT_TYPES = new Set(['Video', 'Audio']);

/**
 * Why a length can't be saved for this content type, or null when it can: the
 * API's rule (EcContent#length_is_readable). A filled-in length must be
 * readable. Video and Audio need a nonzero one, since zero is what an unknown
 * length looks like to the players; other types may leave it blank or zero.
 */
export const contentLengthError = (contentType: string, value: string): string | null => {
  const seconds = readContentLengthSeconds(value);
  if (seconds === null && value.trim() !== '') {
    return CONTENT_LENGTH_FORMAT_ERROR;
  }
  if (TIMED_CONTENT_TYPES.has(contentType) && !seconds) {
    return CONTENT_LENGTH_REQUIRED_ERROR;
  }
  return null;
};

/**
 * Seconds as shown to the user: "<1 min" under 30 seconds, otherwise whole
 * minutes rounded to the nearest ("12 min", "62 min").
 */
export const formatMinutes = (seconds: number): string => {
  if (seconds < UNDER_A_MINUTE_SECONDS) return '<1 min';
  return `${Math.round(seconds / 60)} min`;
};

/**
 * A content length as shown to the user, by formatMinutes. Null when the
 * length can't be read (see contentLengthSeconds); callers leave it out.
 */
export const formatContentLength = (length: unknown): string | null => {
  const seconds = contentLengthSeconds(length);
  return seconds === null ? null : formatMinutes(seconds);
};

/**
 * Total seconds of the items' lengths; an unreadable length adds nothing.
 * (misc_functions' sumContentLengths returns an "h:mm:ss" string instead.)
 */
export const sumLengthSeconds = (items: ReadonlyArray<{ length?: unknown }>): number =>
  items.reduce((total, item) => total + (contentLengthSeconds(item.length) ?? 0), 0);

/**
 * A total running time, rounded to the nearest minute: "<1 min", "30 min",
 * "3 hr" for whole hours, "3 hr 22 min". Null when there is no time to show.
 */
export const formatTotalLength = (seconds: number): string | null => {
  if (!(seconds > 0)) return null;
  const minutes = Math.round(seconds / 60);
  if (minutes === 0) return '<1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
};
